import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import semver from 'semver';
import ControlSelect from '@mapbox/mr-ui/control-select';
import Icon from '@mapbox/mr-ui/icon';
import { softwareService } from '../util/mapbox-client';

const MAC_OS = 'macos';
const LINUX = 'linux';

const getVersions = (beta, installerSoftwareInfo, minVersion) => {
  const RELEASE_REGEX = /^\d+\.\d+\.\d+$/;

  return installerSoftwareInfo
    .get('versions')
    .map(r => r.get('version'))
    .filter(v => RELEASE_REGEX.test(v) === !beta)
    .filter(v => !minVersion || (semver.valid(v) && semver.gte(v, minVersion)))
    .sort((v1, v2) => (v1 < v2 ? +1 : v2 < v1 ? -1 : 0)); // sort version descending
};

const getOptions = (installerSoftwareInfo, minVersion) => {
  let releases = getVersions(false, installerSoftwareInfo, minVersion)
    .map(v => ({ label: v, value: v }))
    .toArray();
  const prereleases = getVersions(true, installerSoftwareInfo, minVersion)
    .map(v => ({ label: v, value: v }))
    .toArray();
  if (releases.length == 0) {
    releases.push({ label: 'None Available', value: 'None', disabled: true });
  }
  if (prereleases.length == 0) {
    return releases;
  }
  return [
    {
      label: 'Releases',
      options: releases
    },
    {
      label: 'Pre-releases',
      options: prereleases
    }
  ];
};

const DownloadGroup = ({
  installerSoftwareInfo,
  latestInstaller,
  minVersion
}) => {
  const options = getOptions(installerSoftwareInfo, minVersion);
  const initialVersion =
    options && options.length > 0 && options[0].options
      ? (options[0].options[0] && options[0].options[0].value) || ''
      : (options[0] && options[0].value) || '';
  const latestAllowed =
    latestInstaller &&
    (!minVersion || semver.gte(latestInstaller.get('version'), minVersion))
      ? latestInstaller.get('version')
      : initialVersion;

  const [selectedVersion, setSelectedVersion] = useState(latestAllowed);

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState({});
  useEffect(() => {
    softwareService
      .getDownloadLink({
        installerVersion: selectedVersion
      })
      .send()
      .then(response => {
        setDownloadLinks(response.body.downloads);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });
  }, [selectedVersion]);

  return (
    <div>
      <div className="mb12">
        <ControlSelect
          id="versionSelection"
          value={selectedVersion}
          label="Select installer version"
          onChange={value => {
            setSelectedVersion(value);
            setIsLoading(true);
            setIsError(false);
          }}
          options={options}
        />
      </div>

      <div className="grid grid--gut12">
        {isError ? (
          'Something went wrong getting the download links - try selecting another version.'
        ) : (
          <>
            <div className="col col--6-mm">
              <button
                disabled={isLoading || !downloadLinks[LINUX]}
                onClick={() => {
                  if (downloadLinks[LINUX])
                    window.location.href = downloadLinks[LINUX];
                }}
                className={'w-full btn px24 round-full txt-s transition'}
              >
                {isLoading ? (
                  <span className="loading loading--s" />
                ) : (
                  <>
                    <Icon themeIcon="mr6" name="arrow-down" inline={true} />
                    Download for Linux
                  </>
                )}
              </button>
            </div>
            <div className="col col--6-mm">
              <button
                disabled={isLoading || !downloadLinks[MAC_OS]}
                onClick={() => {
                  if (downloadLinks[MAC_OS])
                    window.location.href = downloadLinks[MAC_OS];
                }}
                className="w-full btn px24 round-full txt-s transition btn--stroke"
              >
                {isLoading ? (
                  <span className="loading loading--s" />
                ) : (
                  <>
                    <Icon themeIcon="mr6" name="arrow-down" inline={true} />
                    Download for Mac
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

DownloadGroup.propTypes = {
  latestInstaller: PropTypes.object,
  installerSoftwareInfo: PropTypes.instanceOf(Immutable.Map).isRequired,
  minVersion: PropTypes.string
};

export { getVersions, getOptions };
export default DownloadGroup;
